/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, getPayloadObject, getTelemetryObject, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { IHeaderData } from './header.data';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header/src/modules/header/./header';
import { Logo } from './components/Logo';
import debounce from 'lodash/debounce';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<IHeaderData>> = props => {
    // VSI Customization
    const [scroll, setScroll] = React.useState(false);
    React.useEffect(() => {
        window.addEventListener('scroll', () => {
            setScroll(window.scrollY > 0);
        });
    }, []);
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        Divider,
        context: {
            request: {
                user: { isAuthenticated, signInUrl }
            }
        },
        config: { registerNowButton, lookUpTime, setTimeToLogout },
        resources: { hello, login }
    } = props;
    const miniCartIconHide = props.context.request.telemetryPageName === 'CheckoutPage' ? null : props.cartIcon;
    const discountOffer = 'discount-offer';
    const [isMobile, setIsMobile] = React.useState<boolean>();
    const _updateViewport = (): void => {
        const viewport = _getViewport();
        viewport === 'xs' || viewport === 'sm' ? setIsMobile(true) : setIsMobile(false);
    };
    type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

    const _getViewport = (): GridSize => {
        // Always render in mobile viewport on a mobile device
        if (props.context?.request && props.context.request.device && props.context.request.device.Type === 'Mobile') {
            return 'xs';
        }
        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = props.context?.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }
        return 'lg';
    };

    React.useEffect(() => {
        _updateViewport();
        if (MsDyn365.isBrowser && window.addEventListener) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            window.addEventListener('resize', _updateViewport);
        }
    }, []);

    const [lastActivityTime, setLastActivityTime] = React.useState<number>(Date.now());

    const resetIdleTime = () => {
        setLastActivityTime(Date.now());
    };

    function signout() {
        localStorage.setItem('hasSignedOut', 'true');
        setCookie('hasSignedOut', 'true', 1);
        const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
        const payLoad = getPayloadObject('click', telemetryContent, 'Sign out');
        onTelemetryClick(telemetryContent, payLoad, 'Sign out');
        window.location.href = props.context.request.user.signOutUrl!;
    }

    const checkUserActivityTime = () => {
        const currentTime = Date.now();
        if (currentTime - lastActivityTime >= (setTimeToLogout ?? 5 * 60 * 1000)) {
            signout();
        }
    };

    const setCookie = (name: string, value: string, deltaDaysFromToday: number) => {
        const { cookies } = props.context.request;
        const expirationDate = new Date(Date.now());
        expirationDate.setTime(expirationDate.getTime() + deltaDaysFromToday * 24 * 60 * 60 * 1000);
        cookies.set(name, value, {
            expires: deltaDaysFromToday ? expirationDate : null,
            isEssential: true
        } as {});
    };

    const debouncedResetIdleTime = debounce(resetIdleTime, 200);
    const isLoggedIn = props.context.request.user.isAuthenticated;
    React.useEffect(() => {
        if (isLoggedIn) {
            const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

            events.forEach(event => {
                window.addEventListener(event, debouncedResetIdleTime);
            });

            const intervalId = setInterval(checkUserActivityTime, lookUpTime ?? 1000);

            return () => {
                events.forEach(event => {
                    window.removeEventListener(event, debouncedResetIdleTime);
                });
                clearInterval(intervalId);
            };
        }
        return;
    }, [lastActivityTime]);

    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);

    const mobileLogo: React.ReactNode = (
        <Logo
            {...{
                link: props.config.mobileLogoLink,
                image: props.config.mobileLogoImage,
                className: 'ms-header__logo mobile-logo',
                gridSettings: props.context.request.gridSettings,
                requestContext: props.context.request,
                telemetryContent: telemetryContent,
                typeName: props.typeName
            }}
        />
    );

    return (
        <div className={scroll ? 'ms-header__desktop-view_sticky' : 'ms-header__desktop-view'}>
            {/* </div> */}
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        {props.navIcon}
                        {isMobile ? mobileLogo : props.logo}
                        {_renderReactFragment(props.search)}
                        {props.preferredStore}
                        {props.currentDistributor}
                        {/* {_renderDesktopAccountBlock(props)} */}
                        {/* VSI CUSTOMIZATION FOR GUEST USER LINKS STARTS REGISTER ARE CONFIG AND SINGINURL AUTO GENERATED */}
                        {registerNowButton && signInUrl && (
                            <div className='regiser-login-container'>
                                {miniCartIconHide && <span className='_title'>{hello}</span>}
                                {!isAuthenticated ? (
                                    <div className='_links-container'>
                                        <a title='Log in' className='_login-link' href={signInUrl}>
                                            {login}
                                        </a>
                                    </div>
                                ) : (
                                    _renderDesktopAccountBlock(props)
                                )}
                            </div>
                        )}
                        {/* {props.wishListIconDesktop} */}
                        {/* VSI CUSTOMIZATIOMN END */}
                        <Node {...Divider} />
                        {props.config.discountAndOffersText?.linkText && (
                            <div className={`${discountOffer}_container`}>
                                <span className={`${discountOffer}_title`}>{props.config.discountTextHeading}</span>
                                <a className={`${discountOffer}_link`} href={props.config.discountAndOffersText?.linkUrl?.destinationUrl}>
                                    {props.config.discountAndOffersText?.linkText}
                                </a>
                            </div>
                        )}

                        {miniCartIconHide && isAuthenticated ? props.cartIcon : false}

                        {_renderReactFragment(props.siteOptions)}
                    </Node>
                    {_renderReactFragment(props.menuBar)}
                </Node>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        <div className='reponsive-menu-logo'>{isMobile ? mobileLogo : props.logo}</div>
                        {_renderReactFragment(props.search)}
                        {props.MobileMenuHeader}
                        <Node {...MobileMenuLinksContainer}>
                            {props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {props.siteOptions}
                            {props.signInLink}
                            {props.signOutLink}
                            {_renderReactFragment(props.menuBar)}
                            {/* {props.wishListIconMobile} */}
                        </Node>
                    </Node>
                </Node>
            </Module>
        </div>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps & IHeaderProps<IHeaderData>): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;
    const extendedUrl =
        props.context.request.user.signOutUrl! + props.config.signoutExtendedUrl?.trim() ||
        '?ru=https://e-commerce-krannich.commerce.dynamics.com/kaum-au/';

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <span id='myprofilePopover' className='sr-only' />
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {React.cloneElement(signOutLink as React.ReactElement, { href: extendedUrl })}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
